import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Category } from '@pacer/shared';
import { MEMBER_COLORS } from '@pacer/shared';
import * as api from '../../api';
import { Icon, type IconName } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { Button, Card, Field, Input, cx } from '../../components/ui';
import { queryKeys } from '../../state/queries';
import { confirmDelete } from '../../state/ui';

// The icons offered when building a category. Kept in sync with the Icon set.
const CATEGORY_ICONS: IconName[] = [
  'school',
  'activity',
  'briefcase',
  'medical',
  'people',
  'home',
  'cake',
  'cart',
  'note',
  'calendar',
  'today',
  'tag',
];

export function CategoryPanel() {
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const { data: categories = [] } = useQuery({ queryKey: queryKeys.categories, queryFn: api.getCategories });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="font-display text-[20px] text-primary">Event categories</h2>
        <Button variant="primary" size="sm" icon="plus" className="ml-auto" onClick={() => setEditing('new')}>
          Add category
        </Button>
      </div>
      <p className="text-[13px] leading-relaxed text-secondary">
        Group events — School, Sports, Work — each with its own colour and icon. An event's
        category sets its colour on the calendar unless you override it.
      </p>

      {categories.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-subtle px-4 py-8 text-center font-mono text-[12px] text-muted">
          No categories.
        </p>
      ) : (
        <Card className="overflow-hidden">
          <ul>
            {categories.map((cat) => (
              <li key={cat.id} className="border-b border-border-subtle last:border-b-0">
                <button
                  onClick={() => setEditing(cat)}
                  className="touch flex w-full items-center gap-3 px-3 text-left transition-colors hover:bg-surface-high/40"
                >
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full"
                    style={{ background: `color-mix(in srgb, ${cat.color} 16%, transparent)`, color: cat.color }}
                  >
                    {cat.icon ? <Icon name={cat.icon as IconName} size={16} /> : <span className="size-2 rounded-full" style={{ background: cat.color }} />}
                  </span>
                  <span className="flex-1 text-[15px] text-primary">{cat.name}</span>
                  <Icon name="chevron-right" size={14} className="text-muted" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {editing && (
        <CategoryEditor category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function CategoryEditor({ category, onClose }: { category: Category | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(category?.name ?? '');
  const [color, setColor] = useState(category?.color ?? MEMBER_COLORS[0]);
  const [icon, setIcon] = useState<string | null>(category?.icon ?? null);
  const [error, setError] = useState<string | null>(null);

  const done = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.categories });
    await qc.invalidateQueries({ queryKey: ['events'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), color, icon };
      return category ? api.updateCategory(category.id, payload) : api.createCategory(payload);
    },
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteCategory(category!.id),
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={category ? 'Edit' : 'New'}
      title={category ? 'Edit category' : 'New category'}
      footer={
        <>
          {category && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete(`the “${category.name}” category`, 'Events keep their details — they just lose this category.'))
                  remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!name.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-center py-1">
          <span
            className="flex size-16 items-center justify-center rounded-full"
            style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}
          >
            {icon ? <Icon name={icon as IconName} size={30} strokeWidth={1.3} /> : <span className="size-4 rounded-full" style={{ background: color }} />}
          </span>
        </div>

        <Field label="Name">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="School" autoCapitalize="words" />
        </Field>

        <Field label="Icon">
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => setIcon(null)}
              className={cx(
                'flex size-10 items-center justify-center rounded-md border transition-colors',
                icon === null ? 'border-accent text-accent' : 'border-border-subtle text-muted',
              )}
              aria-label="No icon"
            >
              <span className="size-2 rounded-full bg-current" />
            </button>
            {CATEGORY_ICONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={cx(
                  'flex size-10 items-center justify-center rounded-md border transition-colors',
                  icon === ic ? 'border-accent text-accent' : 'border-border-subtle text-secondary hover:text-primary',
                )}
                aria-label={ic}
              >
                <Icon name={ic} size={18} />
              </button>
            ))}
          </div>
        </Field>

        <Field label="Colour">
          <div className="flex flex-wrap gap-2 pt-1">
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className="size-9 rounded-full"
                style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 3 }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
